import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';

import { cn } from '~/lib/cn';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../../components/Table';
import type { SummaryTableProps } from './types';

export function SummaryTable<T extends { isTotal?: boolean }>({
  data,
  columns,
  ariaLabel = 'Summary table',
  maxWidth,
}: SummaryTableProps<T>) {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div
      className="mx-auto w-full"
      style={maxWidth ? { maxWidth } : undefined}
      data-testid="summary-table-wrapper"
    >
      <Table aria-label={ariaLabel} className="w-full table-fixed">
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header, headerIndex) => (
                <TableHead
                  key={header.id}
                  className={cn(
                    'whitespace-nowrap px-sm text-body-sm font-normal first:pl-lg last:pr-lg',
                    headerIndex === 0 ? 'text-left' : 'text-right',
                  )}
                  style={{ width: header.getSize() }}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>

        <TableBody>
          {table.getRowModel().rows.map((row, index) => {
            const isTotal = (row.original as { isTotal?: boolean }).isTotal;

            return (
              <TableRow
                key={row.id}
                className={cn(isTotal && 'border-t border-emphasis-subtle')}
              >
                {row.getVisibleCells().map((cell, cellIndex) => (
                  <TableCell
                    key={cell.id}
                    className={cn(
                      'border-y py-md border-transparent',
                      'first:rounded-l-sm first:border-l first:pl-lg',
                      'last:rounded-r-sm last:border-r last:pr-lg',
                      !isTotal &&
                        index % 2 === 0 &&
                        'bg-default-inverse/default',
                    )}
                    style={{ width: cell.column.getSize() }}
                  >
                    <div
                      className={cn(
                        'flex items-center gap-sm px-sm',
                        cellIndex > 0 && 'justify-end',
                      )}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </div>
                  </TableCell>
                ))}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

SummaryTable.displayName = 'SummaryTable';
